import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { apiDomain } from "configs/configs";
import { logout } from "helpers/helpers";
import {
  Flex,
  Input,
  Checkbox,
  Form,
  Row,
  Col,
  Typography,
  Button,
  notification,
} from "antd";
import styles from "pages/admin/referenceBooks/surfaces/surfaces.module.scss";
import clsx from "clsx";
import MobileInnerWrapper from "layouts/mobileInnerWrapper/mobileInnerWrapper";
import Table, { ColumnsType } from "components/table/table";
import { useParams, useNavigate } from "react-router-dom";
import { create } from "zustand";

const { Text } = Typography;

type ComponentProps = {};

type Surface = {
  id: number;
  name: string;
  status: "active" | "unactive";
  styles?: {
    [className: string]: string;
  };
};

const { Title } = Typography;

type SurfacesStoreState = {
  list: { isLoaded: boolean; data: Surface[] };
  setList: (list: { isLoaded: boolean; data: Surface[] }) => void;
  putIds: number[];
  setPutIds: (update: (state: number[]) => number[]) => void;
  isPostInProcess: boolean;
  setIsPostInProcess: (isPostInProcess: boolean) => void;
  isSubmittable: boolean;
  setIsSubmittable: (isSubmittable: boolean) => void;
  isError: boolean;
  setIsError: (isError: boolean) => void;
};

const useSurfacesStore = create<SurfacesStoreState>((set) => ({
  list: {
    isLoaded: false,
    data: [],
  },
  setList: (list) => set({ list }),
  putIds: [],
  setPutIds: (update) =>
    set((state) => ({
      putIds: update(state.putIds),
    })),
  isPostInProcess: false,
  setIsPostInProcess: (isPostInProcess) => set({ isPostInProcess }),
  isSubmittable: false,
  setIsSubmittable: (isSubmittable) => set({ isSubmittable }),
  isError: false,
  setIsError: (isError) => set({ isError }),
}));

const Page: React.FC<ComponentProps> = () => {
  const { "*": subRoute } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const {
    list,
    setList,
    putIds,
    setPutIds,
    isPostInProcess,
    setIsPostInProcess,
    isSubmittable,
    setIsSubmittable,
    isError,
    setIsError,
  } = useSurfacesStore((store) => store);

  const listRef = useRef(list);

  const isAddMode = useMemo(() => {
    if (subRoute === "add") return true;

    return false;
  }, [subRoute]);

  const isEditMode = useMemo(() => {
    if (!subRoute?.startsWith("edit/")) return false;

    const id = parseInt(subRoute.split("/")[1], 10);

    return isNaN(id) ? false : id;
  }, [subRoute]);

  //  Error while saving change status
  const openNotificationWithError = useCallback(
    ({ message }: { message: string }) => {
      notification.error({
        message: "Ошибка",
        description: message,
        placement: "topRight",
        duration: 3,
      });
    },
    []
  );

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    listRef.current = list;
  }, [list]);

  //  Effect triggers when mode changes to edit or add. Also when some value changes. It fires validation and reset error.
  useEffect(() => {
    if (!isAddMode && !isEditMode) return;

    setIsError(false);

    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [isAddMode, isEditMode, form, values, setIsError, setIsSubmittable]);

  //  Get all surfaces
  const getList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/surfaces`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: Surface[] = data.records;

          setList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (list.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setList, list.isLoaded]
  );

  //  When started, get all surfaces
  useEffect(() => {
    getList();
  }, [getList]);

  //  Edit button sets edit mode or reset it
  const handleEditButton = useCallback(
    (id?: number) => {
      navigate(`/reference-books/surfaces/edit/${id}`);
    },
    [navigate]
  );

  //  Triggers when isEditMode changed. Set initial fields value in form or reset it. Reset error.
  useEffect(() => {
    const item = listRef.current.data.find((item) => item.id === isEditMode);

    if (!item) form.resetFields();

    if (item)
      form.setFieldsValue({
        name: item.name,
        status: item.status === "active",
      });

    setIsError(false);

    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [isEditMode, form, list.isLoaded, setIsError, setIsSubmittable]);

  //  Add button sets add mode or reset it
  const handleAddButton = useCallback(() => {
    navigate(`/reference-books/surfaces/add`);
  }, [navigate]);

  const handleBackButton = useCallback(() => {
    navigate(`/reference-books/surfaces`);
  }, [navigate]);

  const isInProcess = useMemo(() => {
    if (!!isAddMode && !!isPostInProcess) return true;

    if (!!isEditMode && !!putIds.includes(isEditMode)) return true;

    return false;
  }, [isAddMode, isPostInProcess, isEditMode, putIds]);

  //  Submit form, depends of mode
  const handleFinish = useCallback(() => {
    if (isInProcess) return;

    const fetchData = async () => {
      setIsError(false);

      const editModeId = isEditMode;

      if (editModeId)
        setPutIds((state) => {
          return [...state, editModeId];
        });

      if (!editModeId) setIsPostInProcess(true);

      const url = editModeId
        ? `${apiDomain}/api/records/surfaces/${editModeId}`
        : `${apiDomain}/api/records/surfaces`;

      const options: RequestInit = {
        method: editModeId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: values.name.trim(),
          status: values.status ? "active" : "unactive",
        }),
        credentials: "include",
      };

      try {
        const response = await fetch(url, options);

        if (!response.ok) throw new Error();

        getList(true);

        form.resetFields();
        form
          .validateFields({ validateOnly: true })
          .then(() => setIsSubmittable(true))
          .catch(() => setIsSubmittable(false));

        navigate(`/reference-books/surfaces`);

        if (editModeId)
          setPutIds((state) => {
            return state.filter((id) => id !== editModeId);
          });

        if (!editModeId) setIsPostInProcess(false);
      } catch (error) {
        setIsError(true);

        if (editModeId)
          setPutIds((state) => {
            return state.filter((id) => id !== editModeId);
          });

        if (!editModeId) setIsPostInProcess(false);
      }
    };

    fetchData();
  }, [
    isEditMode,
    form,
    values,
    isInProcess,
    navigate,
    setIsError,
    getList,
    setPutIds,
    setIsSubmittable,
    setIsPostInProcess,
  ]);

  //  Change status request
  const handleChangeStatus = useCallback(
    (id: number) => {
      const currentItem = list.data.find((item) => item.id === id);

      if (!currentItem) return;

      setPutIds((state) => {
        return [...state, currentItem.id];
      });

      const fetchData = async () => {
        const url = `${apiDomain}/api/records/surfaces/${currentItem.id}`;
        const options: RequestInit = {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: currentItem.status === "active" ? "unactive" : "active",
          }),
          credentials: "include",
        };

        try {
          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          await getList(true);

          setPutIds((state) => {
            return state.filter((id) => id !== currentItem.id);
          });
        } catch (error) {
          setPutIds((state) => {
            return state.filter((id) => id !== currentItem.id);
          });

          openNotificationWithError({
            message: "Ошибка при изменении статуса",
          });
        }
      };

      fetchData();
    },
    [list, getList, setPutIds, openNotificationWithError]
  );

  //  Set columns for table
  const columns: ColumnsType = useMemo(() => {
    const result: ColumnsType = [
      {
        title: "id",
        key: "id",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as Surface;
          const recordStyles = record.styles;

          return (
            <div
              className={clsx(
                record.status === "unactive" && recordStyles?.disabledRow
              )}
            >
              {record.id}
            </div>
          );
        },
      },
      {
        title: "Название",
        key: "name",
        width: "100%",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as Surface;
          const recordStyles = record.styles;

          return (
            <div
              className={clsx(
                record.status === "unactive" && recordStyles?.disabledRow
              )}
            >
              {record.name}
            </div>
          );
        },
      },
      {
        key: "edit",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as Surface;
          const recordStyles = record.styles;

          return (
            <>
              <Button
                onClick={() => handleEditButton(record.id)}
                type={record.status === "active" ? "primary" : "default"}
                className={recordStyles?.actionButton}
                disabled={isInProcess}
              >
                Изменить
              </Button>
            </>
          );
        },
      },
      {
        key: "changeStatus",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as Surface;
          const recordStyles = record.styles;

          return (
            <>
              <Button
                onClick={() => handleChangeStatus(record.id)}
                type={record.status === "active" ? "primary" : "default"}
                danger={record.status === "active"}
                loading={putIds.includes(record.id)}
                className={recordStyles?.actionButton}
                disabled={isInProcess}
              >
                {record.status === "active" ? "Выключить" : "Включить"}
              </Button>
            </>
          );
        },
      },
    ];

    return result;
  }, [putIds, handleEditButton, handleChangeStatus, isInProcess]);

  return (
    <MobileInnerWrapper>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} className={styles.title}>
            {isAddMode
              ? `Добавление типа поверхности`
              : isEditMode
              ? `Редактирование типа поверхности №${isEditMode}`
              : `Список типов поверхностей`}
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={
              isAddMode || isEditMode ? handleBackButton : handleAddButton
            }
            disabled={isInProcess}
          >
            {isAddMode || isEditMode
              ? `Вернуться к списку`
              : `Добавить тип поверхности`}
          </Button>
        </Col>
      </Row>
      {(!!isAddMode || !!isEditMode) && (
        <>
          <Flex vertical gap={16}>
            <Form
              layout={"vertical"}
              form={form}
              onFinish={handleFinish}
              initialValues={{
                name: "",
                status: true,
              }}
            >
              <Form.Item
                name="name"
                label="Название типа поверхности"
                rules={[
                  {
                    required: true,
                    message: "Пожалуйста, введите название типа поверхности!",
                  },
                ]}
                validateTrigger={false}
                required={false}
              >
                <Input
                  size="large"
                  name="name"
                  disabled={isInProcess || !list.isLoaded}
                />
              </Form.Item>
              <Form.Item name="status" valuePropName="checked">
                <Checkbox disabled={isInProcess || !list.isLoaded}>
                  Тип поверхности включен
                </Checkbox>
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  size="large"
                  className={styles.button}
                  disabled={!isSubmittable}
                  htmlType="submit"
                  loading={isInProcess || !list.isLoaded}
                >
                  Сохранить
                </Button>
              </Form.Item>
              {!!isError && (
                <Form.Item>
                  <Text type="danger">
                    Не удалось сохранить тип поверхности
                  </Text>
                </Form.Item>
              )}
            </Form>
          </Flex>
        </>
      )}
      {!isAddMode && !isEditMode && (
        <Table
          dataSource={list.data}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={!list.isLoaded}
          styles={styles}
        />
      )}
    </MobileInnerWrapper>
  );
};

export default Page;
